(() => {
  'use strict';

  const templateSelector = '[data-video-template]';
  const loadingClass = 'is-loading';
  const mobileQuery = '(max-width: 749px)';
  let mobileLoadIntent;

  function waitForMobileLoadIntent() {
    if (!window.matchMedia(mobileQuery).matches) return Promise.resolve();
    if (mobileLoadIntent) return mobileLoadIntent;

    mobileLoadIntent = new Promise((resolve) => {
      const events = ['pointerdown', 'touchstart', 'keydown'];
      const allowVideoLoading = () => {
        events.forEach((eventName) => document.removeEventListener(eventName, allowVideoLoading));
        window.removeEventListener('scroll', allowVideoLoading);
        resolve();
      };

      events.forEach((eventName) =>
        document.addEventListener(eventName, allowVideoLoading, {passive: true, once: true})
      );
      window.addEventListener('scroll', allowVideoLoading, {passive: true, once: true});
    });

    return mobileLoadIntent;
  }

  if (customElements.get('video-background')) return;

  customElements.define(
    'video-background',
    class VideoBackground extends HTMLElement {
      connectedCallback() {
        this.videoTemplate = this.querySelector(templateSelector);
        this.video = null;
        this.handleFallbackPlay = this.handleFallbackPlay.bind(this);

        const saveData = navigator.connection?.saveData === true;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!this.dataset.videoId || !this.videoTemplate || saveData || reduceMotion) {
          this.classList.remove(loadingClass);
          return;
        }

        waitForMobileLoadIntent().then(() => {
          if (this.isConnected) this.observeForRender();
        });
      }

      observeForRender() {
        this.renderObserver = new IntersectionObserver(
          (entries, observer) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;

            observer.disconnect();
            this.renderVideo();
          },
          {rootMargin: '0px', threshold: 0.01}
        );

        this.renderObserver.observe(this);
      }

      renderVideo() {
        this.innerHTML = this.videoTemplate.innerHTML;
        this.classList.remove(loadingClass);
        this.video = this.querySelector('video');

        if (!this.video) return;

        this.video.preload = 'none';
        this.observePlayback();
        this.playVideo();
      }

      playVideo() {
        const playPromise = this.video?.play();
        if (!playPromise) return;

        playPromise.catch(() => {
          document.addEventListener('click', this.handleFallbackPlay, {passive: true, once: true});
          document.addEventListener('touchstart', this.handleFallbackPlay, {passive: true, once: true});
        });
      }

      handleFallbackPlay() {
        this.playVideo();
      }

      observePlayback() {
        this.playObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.playVideo();
              } else {
                this.video?.pause();
              }
            });
          },
          {threshold: 0.25}
        );

        this.playObserver.observe(this.video);
      }

      disconnectedCallback() {
        this.renderObserver?.disconnect();
        this.playObserver?.disconnect();
        document.removeEventListener('click', this.handleFallbackPlay);
        document.removeEventListener('touchstart', this.handleFallbackPlay);
      }
    }
  );
})();
